import { alpha, createTheme } from "@mui/material/styles";
import {
  ALERT_RED,
  DARK_SURFACES,
  INFO_BLUE,
  layoutTokens,
  OFF_WHITE,
  SUCCESS_GREEN,
  switchTokens,
  WARNING_GOLD,
} from "@/shared/theme";

// Kept as a compatibility export while callers migrate to @/shared/theme.
export * from "@/shared/theme";

/**
 * Chessla theme — minimalist, high-contrast, chess-inspired.
 * Sharp edges (no border-radius), thick borders, pixelated monospace
 * font for that retro chess-computer aesthetic.
 */
export const theme = createTheme({
  palette: {
    mode: "dark",
    primary: OFF_WHITE,
    secondary: ALERT_RED,
    error: ALERT_RED,
    success: SUCCESS_GREEN,
    warning: WARNING_GOLD,
    info: INFO_BLUE,
    background: { default: DARK_SURFACES.canvas, paper: DARK_SURFACES.panel },
    text: { primary: OFF_WHITE.main, secondary: OFF_WHITE.dark },
    divider: "rgba(255,255,255,0.12)",
  },
  typography: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Source Code Pro', 'Noto Sans Symbols 2', monospace",
    fontSize: 13,
    h1: { fontWeight: 800, letterSpacing: "-.09em", lineHeight: 0.84 },
    h2: { fontWeight: 800, letterSpacing: "-.075em", lineHeight: 0.9 },
    h6: { fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase" },
    subtitle1: { fontWeight: 700, letterSpacing: "0.02em" },
    body2: { lineHeight: 1.6 },
  },
  shape: { borderRadius: 0 }, // NO rounded corners anywhere
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "@media (max-width: 899.95px)": {
          "html, body, #root": {
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          },
        },
        "@media (prefers-reduced-motion: reduce)": {
          html: { scrollBehavior: "auto" },
          "*, *::before, *::after": {
            animationDuration: "0.01ms!important",
            animationIterationCount: "1!important",
            transitionDuration: "0.01ms!important",
            scrollBehavior: "auto!important",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { border: "2px solid rgba(255,255,255,0.1)", borderRadius: 0 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme: buttonTheme }) => ({
          minHeight: 40,
          padding: "8px 16px",
          borderRadius: 0,
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          [buttonTheme.breakpoints.down("sm")]: {
            minHeight: 44,
            padding: "6px 10px",
            fontSize: "0.72rem",
          },
        }),
        sizeSmall: ({ theme: buttonTheme }) => ({
          minHeight: 32,
          padding: "4px 8px",
          [buttonTheme.breakpoints.down("sm")]: { minHeight: 44 },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme: iconTheme }) => ({
          borderRadius: 0,
          [iconTheme.breakpoints.down("sm")]: { minWidth: 44, minHeight: 44 },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { "& .MuiOutlinedInput-root": { borderRadius: 0 } },
      },
    },
    MuiSwitch: {
      defaultProps: { color: "secondary", disableRipple: true },
      styleOverrides: {
        root: ({ theme: switchTheme, ownerState }) => {
          const size = switchTokens[ownerState.size === "small" ? "small" : "medium"];
          const inset = (size.trackHeight - size.thumb) / 2;
          const accent =
            ownerState.color === "default"
              ? switchTheme.palette.primary.main
              : switchTheme.palette[ownerState.color ?? "secondary"].main;
          return {
            width: size.width,
            height: size.height,
            flexShrink: 0,
            "&:hover:has(.MuiSwitch-switchBase:not(.Mui-disabled))": {
              backgroundColor: switchTheme.palette.action.hover,
            },
            padding: `${(size.height - size.trackHeight) / 2}px ${(size.width - size.trackWidth) / 2}px`,
            "& .MuiSwitch-switchBase": {
              padding: `${(size.height - size.thumb) / 2}px ${(size.width - size.trackWidth) / 2 + inset}px`,
              borderRadius: switchTheme.shape.borderRadius,
              color: switchTheme.palette.text.secondary,
              "&:hover, &.Mui-checked:hover": { backgroundColor: "transparent" },
              "&.Mui-focusVisible": {
                outline: `${layoutTokens.structuralBorder}px solid ${accent}`,
                outlineOffset: -layoutTokens.structuralBorder,
              },
              "&.Mui-checked": {
                transform: `translateX(${size.trackWidth - size.thumb - 2 * inset}px)`,
                color: switchTheme.palette.background.default,
                "& + .MuiSwitch-track": { backgroundColor: accent, borderColor: accent, opacity: 1 },
              },
              "&.Mui-disabled": {
                color: switchTheme.palette.action.disabled,
                "& + .MuiSwitch-track": {
                  backgroundColor: switchTheme.palette.action.disabledBackground,
                  borderColor: switchTheme.palette.action.disabled,
                  opacity: 1,
                },
              },
            },
            "& .MuiSwitch-thumb": {
              width: size.thumb,
              height: size.thumb,
              borderRadius: switchTheme.shape.borderRadius,
              boxShadow: "none",
            },
            "& .MuiSwitch-track": {
              borderRadius: switchTheme.shape.borderRadius,
              border: `${layoutTokens.dividerBorder}px solid ${switchTheme.palette.text.secondary}`,
              backgroundColor: switchTheme.palette.background.default,
              boxSizing: "border-box",
              opacity: 1,
            },
          };
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme: dialogTheme }) => ({
          backgroundColor: dialogTheme.palette.background.default,
          backgroundImage: "none",
          color: dialogTheme.palette.text.primary,
          border: `2px solid ${dialogTheme.palette.primary.main}`,
          borderRadius: 0,
          boxShadow: "0 16px 48px rgba(0,0,0,0.55)",
          margin: dialogTheme.spacing(1),
          width: `calc(100% - ${dialogTheme.spacing(2)})`,
          maxHeight: `calc(100% - ${dialogTheme.spacing(2)})`,
          [dialogTheme.breakpoints.up("sm")]: {
            margin: dialogTheme.spacing(3),
            width: "auto",
            maxHeight: `calc(100% - ${dialogTheme.spacing(6)})`,
          },
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ theme: backdropTheme }) => ({
          backgroundColor: alpha(backdropTheme.palette.background.default, 0.78),
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme: dialogTheme }) => ({
          padding: 12,
          color: dialogTheme.palette.primary.main,
          fontSize: "0.8rem",
          fontWeight: 700,
          letterSpacing: "0.06em",
          [dialogTheme.breakpoints.up("sm")]: {
            padding: 16,
            fontSize: "0.9rem",
          },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 12,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "@media (min-width: 600px)": { padding: 16 },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme: dialogTheme }) => ({
          gap: 8,
          padding: 12,
          alignItems: "stretch",
          flexDirection: "column",
          "& > :not(style) ~ :not(style)": { marginLeft: 0 },
          "& > .MuiButton-root": {
            width: "100%",
            minHeight: 48,
            fontSize: "0.8rem",
          },
          "@media (min-width: 600px)": { padding: 16 },
          [dialogTheme.breakpoints.up("sm")]: {
            alignItems: "center",
            flexDirection: "row",
            "& > .MuiButton-root": { width: "auto", minHeight: 40, fontSize: "inherit" },
          },
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme: alertTheme, ownerState }) => {
          const accent = alertTheme.palette[ownerState.severity ?? "info"].main;
          return {
            alignItems: "flex-start",
            border: `1px solid ${alpha(accent, 0.28)}`,
            borderLeft: `3px solid ${accent}`,
            borderRadius: 0,
            backgroundColor: alpha(accent, 0.055),
            color: alertTheme.palette.text.primary,
            padding: "10px 12px",
            boxShadow: "none",
            "& .MuiAlert-icon, & .MuiAlert-action": { color: accent },
          };
        },
        icon: { opacity: 1 },
        action: { paddingTop: 0 },
        message: { padding: "1px 0", fontSize: "0.76rem", lineHeight: 1.55 },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        enterTouchDelay: 0,
        leaveTouchDelay: 5000,
      },
      styleOverrides: {
        tooltip: {
          maxWidth: 280,
          padding: "8px 12px",
          backgroundColor: DARK_SURFACES.panel,
          color: OFF_WHITE.main,
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 0,
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Source Code Pro', monospace",
          fontSize: "0.72rem",
          lineHeight: 1.45,
        },
        arrow: {
          color: DARK_SURFACES.panel,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          padding: 8,
          backgroundColor: DARK_SURFACES.panel,
          color: OFF_WHITE.main,
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 0,
          boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Source Code Pro', monospace",
        },
      },
    },
  },
});
